"use client";
import dynamic from "next/dynamic";
import { useEffect, useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from "gsap";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

// Swiper is ~40 KB gz and this slider sits well below the fold, so it loads on
// demand rather than in the route's first-load bundle. The placeholder holds
// the slider's own height so nothing moves when it swaps in.
const CurvedSlider = dynamic(
  () => import("@/components/animations/CurvedSlider"),
  // Heights mirror .curved-gallery-swiper in globals.css (210 / 320 / 650).
  {
    loading: () => (
      <div className="h-[210px] w-full md:h-[320px] lg:h-[650px]" />
    ),
  },
);

const AboutGallery = () => {
  const hostRef = useRef<HTMLElement>(null);

  // The slider arrives after hydration, so every pinned section below it was
  // measured against the placeholder. The placeholder matches the slider's
  // height, but a font swap or an image load can still move it by a pixel, and
  // a stale ScrollTrigger start is what makes a pinned section fire late.
  // Refresh when this section's own height actually changes, batched into a
  // frame so a resize storm costs one refresh.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let frame = 0;
    let lastHeight = host.getBoundingClientRect().height;

    const observer = new ResizeObserver(() => {
      const height = host.getBoundingClientRect().height;
      if (Math.abs(height - lastHeight) < 1) return;
      lastHeight = height;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => ScrollTrigger.refresh());
    });
    observer.observe(host);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <section ref={hostRef} className="relative w-full">
      <CurvedSlider />
    </section>
  );
};

export default AboutGallery;
